import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ContentCopy as ContentCopyIcon,
  Chat as ChatIcon,
  Link as LinkIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import api from '../../api';
import {
  isGroupUnsafeTemplate,
  renderCommunicationTemplate,
} from './renderCommunicationTemplate';

const CHANNELS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'messenger', label: 'Messenger' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
];

/** Maps UI checkboxes → PORTAL_ACTION_TYPES */
const ACTION_OPTIONS = [
  { key: 'submit_maintenance', label: 'Submit Maintenance', field: 'includeMaintenance' },
  { key: 'confirm_access', label: 'Confirm Access', field: 'includeAccess' },
  { key: 'view_documents', label: 'View Documents', field: 'includeDocuments' },
  { key: 'portal_home', label: 'Open Tenant Portal Home', field: 'includePortalHome' },
];

/**
 * ZRF-WTPB-004 + 006 — WhatsApp-ready composer with drafts / generate-links / mark-sent.
 * No Meta send API.
 */
const CommunicationsComposerPage = ({ onBack }) => {
  const [channel, setChannel] = useState('whatsapp');
  const [sendMode, setSendMode] = useState('manual_copy');
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [propertyId, setPropertyId] = useState('');
  const [unitId, setUnitId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [eventTitle, setEventTitle] = useState('');
  const [eventDetails, setEventDetails] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [actionFlags, setActionFlags] = useState({
    includeMaintenance: true,
    includeAccess: true,
    includeDocuments: true,
    includePortalHome: false,
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState('');
  const [draftId, setDraftId] = useState('');
  const [generatedLinks, setGeneratedLinks] = useState([]);
  const [linksReady, setLinksReady] = useState(false);
  const [markedSent, setMarkedSent] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const selectedProperty = useMemo(
    () => properties.find((p) => p.id === propertyId) || null,
    [properties, propertyId]
  );

  const units = useMemo(() => {
    if (!selectedProperty) return [];
    return Array.isArray(selectedProperty.units) ? selectedProperty.units : [];
  }, [selectedProperty]);

  const tenantsForScope = useMemo(() => {
    if (!propertyId) return tenants;
    return tenants.filter((t) => {
      const leases = Array.isArray(t.leases) ? t.leases : [];
      if (leases.length === 0) {
        return !t.propertyId || t.propertyId === propertyId;
      }
      return leases.some((lease) => {
        if (lease.propertyId !== propertyId) return false;
        if (unitId && lease.unitId && lease.unitId !== unitId) return false;
        return true;
      });
    });
  }, [tenants, propertyId, unitId]);

  const selectedTenant = useMemo(
    () => tenantsForScope.find((t) => t.id === tenantId) || null,
    [tenantsForScope, tenantId]
  );

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === templateId) || null,
    [templates, templateId]
  );

  const allowedActions = useMemo(() => {
    const allowed = selectedTemplate?.allowedActionTypes;
    if (!Array.isArray(allowed) || allowed.length === 0) return ACTION_OPTIONS;
    return ACTION_OPTIONS.filter((opt) => allowed.includes(opt.key));
  }, [selectedTemplate]);

  const selectedActionTypes = useMemo(
    () => allowedActions.filter((opt) => actionFlags[opt.field]).map((opt) => opt.key),
    [allowedActions, actionFlags]
  );

  const groupUnsafe = isGroupUnsafeTemplate(selectedTemplate);

  const invalidateDraft = useCallback(() => {
    setDraftId('');
    setGeneratedLinks([]);
    setLinksReady(false);
    setMarkedSent(false);
  }, []);

  const buildLocalPreview = useCallback(
    (template, flags) => {
      if (!template?.body) return '';
      return renderCommunicationTemplate(template.body, {
        propertyName: selectedProperty?.name || selectedProperty?.address || '',
        tenantFirstName: selectedTenant?.firstName || '',
        eventTitle: eventTitle.trim(),
        eventDetails: eventDetails.trim(),
        eventDate: eventDate.trim(),
        includeMaintenance: flags.includeMaintenance,
        includeAccess: flags.includeAccess,
        includeDocuments: flags.includeDocuments,
        includePortalHome: flags.includePortalHome,
      });
    },
    [selectedProperty, selectedTenant, eventTitle, eventDetails, eventDate]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError('');
      try {
        const [propsRes, templatesRes, tenantsRes] = await Promise.all([
          api.get('/api/properties'),
          api.get('/api/communications/templates'),
          api.get('/api/tenants').catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;

        const props = Array.isArray(propsRes.data)
          ? propsRes.data
          : propsRes.data?.properties || [];
        setProperties(props);

        const tplPayload = templatesRes.data;
        const tplList = Array.isArray(tplPayload)
          ? tplPayload
          : tplPayload?.templates || [];
        setTemplates(tplList);
        if (tplList.length > 0) setTemplateId(tplList[0].id);

        const tenantList = Array.isArray(tenantsRes.data)
          ? tenantsRes.data
          : tenantsRes.data?.tenants || [];
        setTenants(tenantList);
        console.log('✅ Communications composer data loaded', {
          properties: props.length,
          templates: tplList.length,
          tenants: tenantList.length,
        });
      } catch (err) {
        console.error('❌ Failed to load composer data:', err);
        if (!cancelled) {
          const status = err?.response?.status;
          setLoadError(
            status === 404
              ? 'Communications bridge is off on the API, or templates are unavailable (404).'
              : 'Could not load properties or templates. Check API and COMMUNICATIONS_BRIDGE_ENABLED.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-seed local preview when template / identity changes
  useEffect(() => {
    invalidateDraft();
    if (!selectedTemplate) {
      setMessageBody('');
      return;
    }
    const nextFlags = { ...actionFlags };
    ACTION_OPTIONS.forEach((opt) => {
      const allowed =
        !selectedTemplate.allowedActionTypes?.length ||
        selectedTemplate.allowedActionTypes.includes(opt.key);
      if (!allowed) nextFlags[opt.field] = false;
    });
    setActionFlags(nextFlags);
    setMessageBody(buildLocalPreview(selectedTemplate, nextFlags));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed on identity change only
  }, [templateId, propertyId, tenantId, unitId, selectedTemplate?.id]);

  const handleActionToggle = (field) => (event) => {
    const next = { ...actionFlags, [field]: event.target.checked };
    setActionFlags(next);
    invalidateDraft();
    if (selectedTemplate) {
      setMessageBody(buildLocalPreview(selectedTemplate, next));
    }
  };

  const handleRefreshPreview = () => {
    if (!selectedTemplate) return;
    invalidateDraft();
    setMessageBody(buildLocalPreview(selectedTemplate, actionFlags));
  };

  const resolveScope = () => {
    if (tenantId) return 'tenant';
    if (unitId) return 'unit';
    return 'property';
  };

  const handleGenerateLinks = async () => {
    if (!propertyId) {
      setSnackbar({ open: true, message: 'Select a property first.', severity: 'warning' });
      return;
    }
    if (!selectedTemplate?.body) {
      setSnackbar({ open: true, message: 'Select a template first.', severity: 'warning' });
      return;
    }
    if (selectedActionTypes.length === 0) {
      setSnackbar({
        open: true,
        message: 'Select at least one action link to generate.',
        severity: 'warning',
      });
      return;
    }

    setBusy('links');
    try {
      const draftRes = await api.post('/api/communications/drafts', {
        channel,
        propertyId,
        scope: resolveScope(),
        unitId: unitId || undefined,
        unitIds: unitId ? [unitId] : [],
        tenantId: tenantId || undefined,
        tenantIds: tenantId ? [tenantId] : [],
        templateId: selectedTemplate.id,
        body: selectedTemplate.body,
        actionTypes: selectedActionTypes,
        groupSafe: selectedTemplate.groupSafe,
        requiresPrivateChannel: selectedTemplate.requiresPrivateChannel,
        category: selectedTemplate.category,
        propertyName: selectedProperty?.name || selectedProperty?.address || '',
        tenantFirstName: selectedTenant?.firstName || '',
        eventTitle: eventTitle.trim(),
        eventDetails: eventDetails.trim(),
        eventDate: eventDate.trim(),
      });

      const newDraftId =
        draftRes.data?.communicationDraftId ||
        draftRes.data?.id ||
        draftRes.data?.draftId;
      if (!newDraftId) {
        throw new Error('Draft created but no id returned');
      }

      const linksRes = await api.post(`/api/communications/${newDraftId}/generate-links`, {
        actionTypes: selectedActionTypes,
        expiresInDays: 14,
      });

      const rendered = linksRes.data?.renderedBody || '';
      const links = Array.isArray(linksRes.data?.links) ? linksRes.data.links : [];

      setDraftId(newDraftId);
      setGeneratedLinks(links);
      setLinksReady(true);
      setMarkedSent(false);
      if (rendered) {
        setMessageBody(rendered);
      }
      console.log('✅ generate-links ok', { draftId: newDraftId, linkCount: links.length });
      setSnackbar({
        open: true,
        message: `Generated ${links.length} secure link${links.length === 1 ? '' : 's'}`,
        severity: 'success',
      });
    } catch (err) {
      console.error('❌ generate-links failed:', err);
      const msg =
        err?.response?.data?.error || err?.message || 'Failed to generate links';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setBusy('');
    }
  };

  const handleCopy = async () => {
    const text = messageBody.trim();
    if (!text) {
      setSnackbar({
        open: true,
        message: 'Nothing to copy yet — pick a template first.',
        severity: 'warning',
      });
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({
        open: true,
        message: channel === 'whatsapp' ? 'Copied for WhatsApp' : 'Message copied',
        severity: 'success',
      });
      console.log('✅ Communication message copied', { linksReady, draftId });
    } catch (err) {
      console.error('❌ Clipboard copy failed:', err);
      setSnackbar({
        open: true,
        message: 'Copy failed — select the preview text and copy manually.',
        severity: 'error',
      });
    }
  };

  const handleMarkSent = async () => {
    if (!draftId) {
      setSnackbar({
        open: true,
        message: 'Generate links first, then mark as sent.',
        severity: 'warning',
      });
      return;
    }
    setBusy('sent');
    try {
      const res = await api.post(`/api/communications/${draftId}/mark-sent`, {
        channel,
        notes: 'Copied for WhatsApp (manual send)',
      });
      setMarkedSent(true);
      console.log('✅ mark-sent ok', res.data);
      setSnackbar({
        open: true,
        message: 'Marked as sent — logged on the communication timeline.',
        severity: 'success',
      });
    } catch (err) {
      console.error('❌ mark-sent failed:', err);
      const msg =
        err?.response?.data?.error || err?.message || 'Failed to mark as sent';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Button
        onClick={onBack}
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2 }}
      >
        Back to Communications
      </Button>

      <Typography variant="h4" component="h1" gutterBottom>
        New Communication
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Draft a WhatsApp-ready message, generate secure portal links, copy, then mark as sent. No
        Meta send API in Phase 1.
      </Typography>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          mb: 3,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <ChatIcon color="primary" />
          <Typography variant="h6" component="h2">
            Composer
          </Typography>
        </Stack>
        <Divider sx={{ mb: 3 }} />

        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">Channel</FormLabel>
          <RadioGroup row value={channel} onChange={(e) => setChannel(e.target.value)}>
            {CHANNELS.map((c) => (
              <FormControlLabel
                key={c.value}
                value={c.value}
                control={<Radio />}
                label={c.label}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <FormControl component="fieldset" sx={{ mb: 3, display: 'block' }}>
          <FormLabel component="legend">Send mode</FormLabel>
          <RadioGroup row value={sendMode} onChange={(e) => setSendMode(e.target.value)}>
            <FormControlLabel value="manual_copy" control={<Radio />} label="Copy manually" />
            <FormControlLabel
              value="api_send"
              control={<Radio />}
              label="Send through API"
              disabled
            />
          </RadioGroup>
          <Typography variant="caption" color="text.secondary">
            API send is Coming soon — Phase 1 is copy-only (no Meta / WhatsApp Cloud API).
          </Typography>
        </FormControl>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Scope
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
          <FormControl fullWidth required>
            <InputLabel id="comm-property-label">Property</InputLabel>
            <Select
              labelId="comm-property-label"
              label="Property"
              value={propertyId}
              onChange={(e) => {
                setPropertyId(e.target.value);
                setUnitId('');
                setTenantId('');
              }}
            >
              <MenuItem value="">
                <em>Select property</em>
              </MenuItem>
              {properties.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name || p.address || p.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!propertyId || units.length === 0}>
            <InputLabel id="comm-unit-label">Unit (optional)</InputLabel>
            <Select
              labelId="comm-unit-label"
              label="Unit (optional)"
              value={unitId}
              onChange={(e) => {
                setUnitId(e.target.value);
                setTenantId('');
              }}
            >
              <MenuItem value="">
                <em>All units</em>
              </MenuItem>
              {units.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.name || u.unitNumber || u.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!propertyId}>
            <InputLabel id="comm-tenant-label">Tenant (optional)</InputLabel>
            <Select
              labelId="comm-tenant-label"
              label="Tenant (optional)"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
            >
              <MenuItem value="">
                <em>All active tenants</em>
              </MenuItem>
              {tenantsForScope.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {[t.firstName, t.lastName].filter(Boolean).join(' ') || t.email || t.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <FormControl fullWidth sx={{ mb: 3 }} disabled={templates.length === 0}>
          <InputLabel id="comm-template-label">Template</InputLabel>
          <Select
            labelId="comm-template-label"
            label="Template"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            {templates.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
                {t.groupSafe === false ? ' · private' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedTemplate?.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedTemplate.description}
          </Typography>
        )}

        <Stack spacing={2} sx={{ mb: 3 }}>
          <TextField
            label="Event title"
            value={eventTitle}
            onChange={(e) => {
              setEventTitle(e.target.value);
              invalidateDraft();
            }}
            fullWidth
            placeholder="Furnace filter replacement"
          />
          <TextField
            label="Event details"
            value={eventDetails}
            onChange={(e) => {
              setEventDetails(e.target.value);
              invalidateDraft();
            }}
            fullWidth
            multiline
            minRows={2}
            placeholder="Thursday, 10:00 AM–12:00 PM"
          />
          <TextField
            label="Event date"
            value={eventDate}
            onChange={(e) => {
              setEventDate(e.target.value);
              invalidateDraft();
            }}
            fullWidth
            placeholder="Thursday, Aug 27"
          />
          <Button size="small" onClick={handleRefreshPreview} disabled={!selectedTemplate}>
            Refresh local preview from template
          </Button>
        </Stack>

        <FormControl component="fieldset" sx={{ mb: 3 }}>
          <FormLabel component="legend">Include action links</FormLabel>
          <FormGroup>
            {allowedActions.map((opt) => (
              <FormControlLabel
                key={opt.key}
                control={
                  <Checkbox
                    checked={Boolean(actionFlags[opt.field])}
                    onChange={handleActionToggle(opt.field)}
                  />
                }
                label={opt.label}
              />
            ))}
          </FormGroup>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Click <strong>Generate Links</strong> to replace placeholders with secure token-only
            portal URLs (no property/tenant IDs in the path).
          </Typography>
        </FormControl>

        {groupUnsafe ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Private channel recommended.</strong> This template may include tenant-specific
            information. Do not paste it into a property group chat unless you remove private
            details. Prefer 1:1 WhatsApp, email, or the tenant portal.
          </Alert>
        ) : selectedTemplate ? (
          <Alert severity="success" sx={{ mb: 3 }}>
            This message is marked group-safe for a property chat — still avoid posting private unit
            details in the group.
          </Alert>
        ) : null}

        {linksReady && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Secure links ready{draftId ? ` (draft ${draftId.slice(0, 8)}…)` : ''}. Preview uses
            token-only URLs from the API.
            {generatedLinks.length > 0 && (
              <Box component="ul" sx={{ m: 0, pl: 2, mt: 1 }}>
                {generatedLinks.map((link) => (
                  <li key={`${link.actionType}-${link.url}`}>
                    <Typography variant="caption" component="span">
                      {link.actionType}: {link.url}
                    </Typography>
                  </li>
                ))}
              </Box>
            )}
          </Alert>
        )}

        {markedSent && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Marked as sent — a timeline event was recorded.
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {channel === 'whatsapp' ? 'WhatsApp preview' : 'Message preview'}
        </Typography>
        <TextField
          value={messageBody}
          onChange={(e) => setMessageBody(e.target.value)}
          fullWidth
          multiline
          minRows={8}
          placeholder="Select a template to preview…"
          sx={{
            mb: 2,
            '& .MuiInputBase-input': { whiteSpace: 'pre-wrap' },
          }}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={busy === 'links' ? <CircularProgress size={16} /> : <LinkIcon />}
            onClick={handleGenerateLinks}
            disabled={Boolean(busy) || !propertyId || !selectedTemplate}
          >
            Generate Links
          </Button>
          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopy}
            disabled={!messageBody.trim() || Boolean(busy)}
          >
            Copy for WhatsApp
          </Button>
          <Button
            variant="outlined"
            startIcon={busy === 'sent' ? <CircularProgress size={16} /> : <SendIcon />}
            onClick={handleMarkSent}
            disabled={!draftId || !linksReady || markedSent || Boolean(busy)}
          >
            Mark as Sent
          </Button>
        </Stack>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommunicationsComposerPage;
